<template>
    <div class="py-12">
        <h1
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ t("vue.tools") }}
        </h1>

        <div class="mt-10">
            <div class="rounded-lg bg-white p-6 shadow w-full max-w-full">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    {{ t("vue.active_report_notifications") }}
                </h2>
                <p class="text-sm text-gray-600 mb-6">
                    {{ t("vue.active_report_notifications_description") }}
                </p>

                <div v-if="notifications.length > 0" class="overflow-x-auto">
                    <div
                        class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                    >
                        <table class="w-full divide-y divide-gray-300">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.name") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.team") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.frequency") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.recipients") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.last_sent") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.next_scheduled") }}
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 bg-white">
                                <tr
                                    v-for="notification in notifications"
                                    :key="notification.id"
                                    class="hover:bg-gray-50"
                                >
                                    <td
                                        class="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs"
                                    >
                                        <div>
                                            <div class="font-medium">
                                                {{ notification.name }}
                                            </div>
                                            <div
                                                class="text-gray-500 text-xs mt-1"
                                            >
                                                {{ notification.description }}
                                            </div>
                                        </div>
                                    </td>
                                    <td
                                        class="px-6 py-4 text-sm text-gray-900 max-w-xs"
                                    >
                                        {{ notification.team_name }}
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            class="capitalize text-sm bg-gray-100 px-2 py-1 rounded"
                                        >
                                            {{ notification.frequency }}
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            class="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded"
                                        >
                                            {{ notification.recipients_count }}
                                            {{ t("vue.recipients") }}
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            v-if="notification.last_sent_at"
                                            class="text-sm text-gray-600"
                                        >
                                            {{ notification.last_sent_at }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-sm text-gray-400"
                                        >
                                            {{ t("vue.never") }}
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            v-if="notification.next_send_at"
                                            class="text-sm text-gray-600"
                                        >
                                            {{ notification.next_send_at }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-sm text-gray-400"
                                        >
                                            {{ t("vue.not_scheduled") }}
                                        </span>
                                    </td>
                                    <td
                                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                                    >
                                        <AtomicButton
                                            color="blue"
                                            size="sm"
                                            :disabled="
                                                sendingNotificationId ===
                                                notification.id
                                            "
                                            :loading="
                                                sendingNotificationId ===
                                                notification.id
                                            "
                                            @click="
                                                handleSendNotification(
                                                    notification,
                                                )
                                            "
                                        >
                                            <template #icon>
                                                <PaperAirplaneIcon
                                                    class="h-5 w-5"
                                                />
                                            </template>
                                            {{ t("vue.send_now") }}
                                        </AtomicButton>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-else class="text-center py-8 text-gray-500">
                    {{ t("vue.no_active_notifications") }}
                </div>
            </div>
        </div>

        <!-- Alert Settings Section -->
        <div class="mt-10">
            <div class="rounded-lg bg-white p-6 shadow w-full max-w-full">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    {{ t("vue.alert_settings") }}
                </h2>
                <p class="text-sm text-gray-600 mb-6">
                    {{ t("vue.alert_settings_description") }}
                </p>

                <div v-if="alertSettings.length > 0" class="overflow-x-auto">
                    <div
                        class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                    >
                        <table class="w-full divide-y divide-gray-300">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.team") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.compliance_threshold") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.time_frame") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.status") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.last_alert") }}
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        {{ t("vue.created_on") }}
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 bg-white">
                                <tr
                                    v-for="alertSetting in alertSettings"
                                    :key="alertSetting.id"
                                    class="hover:bg-gray-50"
                                >
                                    <td
                                        class="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs"
                                    >
                                        {{ alertSetting.team_name }}
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            class="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded"
                                        >
                                            {{ alertSetting.compliance_drop }}%
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            class="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded"
                                        >
                                            {{ alertSetting.time_frame_label }}
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            :class="{
                                                'text-sm px-2 py-1 rounded': true,
                                                'bg-green-100 text-green-800':
                                                    alertSetting.enabled,
                                                'bg-gray-100 text-gray-800':
                                                    !alertSetting.enabled,
                                            }"
                                        >
                                            {{
                                                alertSetting.enabled
                                                    ? t("vue.enabled")
                                                    : t("vue.not_enabled")
                                            }}
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            v-if="alertSetting.last_sent_at"
                                            class="text-sm text-gray-600"
                                        >
                                            {{ alertSetting.last_sent_at }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-sm text-gray-400"
                                        >
                                            {{ t("vue.never") }}
                                        </span>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                                    >
                                        <span
                                            v-if="alertSetting.created_at"
                                            class="text-sm text-gray-600"
                                        >
                                            {{ alertSetting.created_at }}
                                        </span>
                                        <span
                                            v-else
                                            class="text-sm text-gray-400"
                                        >
                                            {{ t("vue.unknown") }}
                                        </span>
                                    </td>
                                    <td
                                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                                    >
                                        <AtomicButton
                                            color="orange"
                                            size="sm"
                                            :disabled="
                                                sendingAlertId ===
                                                alertSetting.id
                                            "
                                            :loading="
                                                sendingAlertId ===
                                                alertSetting.id
                                            "
                                            @click="
                                                handleSendAlert(alertSetting)
                                            "
                                        >
                                            <template #icon>
                                                <ExclamationTriangleIcon
                                                    class="h-5 w-5"
                                                />
                                            </template>
                                            {{ t("vue.send_alert") }}
                                        </AtomicButton>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-else class="text-center py-8 text-gray-500">
                    {{ t("vue.no_alert_settings") }}
                </div>

                <!-- Alert Simulation Button -->
                <div class="mt-6 pt-6 border-t border-gray-200">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-medium text-gray-900">
                                {{ t("vue.alert_simulation") }}
                            </h3>
                            <p class="text-sm text-gray-600">
                                {{ t("vue.alert_simulation_description") }}
                            </p>
                        </div>
                        <AtomicButton
                            color="blue"
                            size="sm"
                            :disabled="simulatingAlerts"
                            :loading="simulatingAlerts"
                            @click="handleSimulateAlerts"
                        >
                            <template #icon>
                                <PlayIcon class="h-5 w-5" />
                            </template>
                            {{ t("vue.simulate_alerts") }}
                        </AtomicButton>
                    </div>

                    <!-- Simulation Results Table -->
                    <div v-if="simulationResults" class="mt-6">
                        <h4 class="text-sm font-medium text-gray-900 mb-3">
                            {{ t("vue.simulation_results") }}
                        </h4>
                        <div class="overflow-x-auto">
                            <div
                                class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                            >
                                <table class="w-full divide-y divide-gray-300">
                                    <thead class="bg-gray-50">
                                        <tr>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.team") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.status") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.compliance_drop") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.triggered_domains") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.skipped_domains") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.logs_to_create") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.emails_to_send") }}
                                            </th>
                                            <th
                                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ t("vue.skip_reason") }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="divide-y divide-gray-200 bg-white"
                                    >
                                        <tr
                                            v-for="result in simulationResults.alert_results"
                                            :key="result.team_id"
                                            class="hover:bg-gray-50"
                                        >
                                            <td
                                                class="px-6 py-4 text-sm font-medium text-gray-900"
                                            >
                                                {{ result.team_name }}
                                            </td>
                                            <td class="px-6 py-4 text-sm">
                                                <span
                                                    v-if="result.success"
                                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                                >
                                                    {{ t("vue.success") }}
                                                </span>
                                                <span
                                                    v-else
                                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                                                >
                                                    {{ t("vue.error") }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-6 py-4 text-sm text-gray-900"
                                            >
                                                <span
                                                    class="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded"
                                                >
                                                    {{
                                                        result.compliance_drop
                                                    }}%
                                                </span>
                                            </td>
                                            <td
                                                class="px-6 py-4 text-sm text-gray-900"
                                            >
                                                <span
                                                    v-if="
                                                        result.triggered_domains
                                                            .length > 0
                                                    "
                                                    class="text-sm bg-green-100 text-green-800 px-2 py-1 rounded"
                                                >
                                                    {{
                                                        result.triggered_domains
                                                            .length
                                                    }}
                                                </span>
                                                <span
                                                    v-else
                                                    class="text-sm bg-gray-100 text-gray-800 px-2 py-1 rounded"
                                                >
                                                    0
                                                </span>
                                            </td>
                                            <td
                                                class="px-6 py-4 text-sm text-gray-900"
                                            >
                                                <span
                                                    v-if="
                                                        result.skipped_domains
                                                            .length > 0
                                                    "
                                                    class="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded"
                                                >
                                                    {{
                                                        result.skipped_domains
                                                            .length
                                                    }}
                                                </span>
                                                <span
                                                    v-else
                                                    class="text-sm bg-gray-100 text-gray-800 px-2 py-1 rounded"
                                                >
                                                    0
                                                </span>
                                            </td>
                                            <td
                                                class="px-6 py-4 text-sm text-gray-900"
                                            >
                                                <span
                                                    class="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded"
                                                >
                                                    {{
                                                        result.logs_to_be_created
                                                    }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-6 py-4 text-sm text-gray-900"
                                            >
                                                <span
                                                    class="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded"
                                                >
                                                    {{
                                                        result.emails_to_be_sent
                                                    }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-6 py-4 text-sm text-gray-900"
                                            >
                                                <span
                                                    v-if="result.skip_reason"
                                                    class="text-sm bg-red-100 text-red-800 px-2 py-1 rounded"
                                                >
                                                    {{ result.skip_reason }}
                                                </span>
                                                <span
                                                    v-else
                                                    class="text-sm bg-gray-100 text-gray-800 px-2 py-1 rounded"
                                                >
                                                    {{ t("vue.none") }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- Summary Stats -->
                        <div class="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div class="bg-blue-50 p-4 rounded-lg">
                                <div class="text-sm font-medium text-blue-900">
                                    {{ t("vue.total_alerts_processed") }}
                                </div>
                                <div class="text-2xl font-bold text-blue-600">
                                    {{
                                        simulationResults.total_alerts_processed
                                    }}
                                </div>
                            </div>
                            <div class="bg-green-50 p-4 rounded-lg">
                                <div class="text-sm font-medium text-green-900">
                                    {{ t("vue.total_logs_to_be_created") }}
                                </div>
                                <div class="text-2xl font-bold text-green-600">
                                    {{
                                        simulationResults.total_logs_to_be_created
                                    }}
                                </div>
                            </div>
                            <div class="bg-purple-50 p-4 rounded-lg">
                                <div
                                    class="text-sm font-medium text-purple-900"
                                >
                                    {{ t("vue.total_emails_to_be_sent") }}
                                </div>
                                <div class="text-2xl font-bold text-purple-600">
                                    {{
                                        simulationResults.total_emails_to_be_sent
                                    }}
                                </div>
                            </div>
                            <div class="bg-orange-50 p-4 rounded-lg">
                                <div
                                    class="text-sm font-medium text-orange-900"
                                >
                                    {{ t("vue.simulation_date") }}
                                </div>
                                <div class="text-sm font-bold text-orange-600">
                                    {{
                                        formatDate(
                                            simulationResults.simulation_date,
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bulk Ecomail Subscription Section -->
        <div class="mt-10">
            <div class="rounded-lg bg-white p-6 shadow w-full max-w-full">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    Bulk Ecomail Subscription
                </h2>
                <p class="text-sm text-gray-600 mb-6">
                    Subscribe all users to the Ecomail mailing list. This
                    operation will process users in batches of 3,000 to ensure
                    optimal performance.
                </p>

                <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <ExclamationTriangleIcon
                                class="h-5 w-5 text-yellow-400"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="ml-3">
                            <p class="text-sm text-yellow-700">
                                <strong>Warning:</strong> This action will
                                subscribe ALL users in the database to Ecomail.
                                Make sure this is what you want to do before
                                proceeding. The operation will run in the
                                background and may take some time to complete
                                depending on the number of users.
                            </p>
                        </div>
                    </div>
                </div>

                <div class="flex justify-start">
                    <AtomicButton
                        color="orange"
                        size="md"
                        :disabled="isBulkSubscribing"
                        :loading="isBulkSubscribing"
                        @click="handleBulkSubscribe"
                    >
                        <template #icon>
                            <UserGroupIcon class="h-5 w-5" />
                        </template>
                        {{
                            isBulkSubscribing
                                ? "Processing..."
                                : "Start Bulk Subscription"
                        }}
                    </AtomicButton>
                </div>

                <div v-if="recentLogs.length > 0" class="mt-8">
                    <h3 class="text-md font-medium text-gray-900 mb-3">
                        {{ t("vue.recent_activity") }}
                    </h3>
                    <div class="space-y-2">
                        <div
                            v-for="log in recentLogs"
                            :key="log.id"
                            class="flex items-center justify-between rounded-md bg-gray-50 px-4 py-2 text-sm"
                        >
                            <div class="flex items-center space-x-3">
                                <div
                                    :class="{
                                        'h-2 w-2 rounded-full': true,
                                        'bg-green-400': log.type === 'success',
                                        'bg-red-400': log.type === 'error',
                                    }"
                                ></div>
                                <span>{{ log.message }}</span>
                            </div>
                            <span class="text-gray-500">{{
                                log.timestamp
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Subscription Data Sync Section -->
        <div class="mt-10">
            <div class="rounded-lg bg-white p-6 shadow w-full max-w-full">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    Sync Subscription Data
                </h2>
                <p class="text-sm text-gray-600 mb-6">
                    Sync subscription data from Paddle to the team database.
                    This updates paddle_sub_items, paddle_sub_ends_at, and
                    domains_quantity fields.
                </p>

                <div class="space-y-4">
                    <!-- Sync Type Selection -->
                    <div>
                        <label
                            class="text-sm font-medium text-gray-900 mb-2 block"
                        >
                            Sync Type
                        </label>
                        <div class="space-y-2">
                            <label class="flex items-center">
                                <input
                                    v-model="syncForm.sync_type"
                                    type="radio"
                                    value="all"
                                    class="mr-3 h-4 w-4 text-indigo-600"
                                />
                                <span class="text-sm text-gray-700">
                                    Sync all teams with subscriptions
                                </span>
                            </label>
                            <label class="flex items-center">
                                <input
                                    v-model="syncForm.sync_type"
                                    type="radio"
                                    value="sub_id"
                                    class="mr-3 h-4 w-4 text-indigo-600"
                                />
                                <span class="text-sm text-gray-700">
                                    Sync by Subscription ID
                                </span>
                            </label>
                            <label class="flex items-center">
                                <input
                                    v-model="syncForm.sync_type"
                                    type="radio"
                                    value="team_id"
                                    class="mr-3 h-4 w-4 text-indigo-600"
                                />
                                <span class="text-sm text-gray-700">
                                    Sync by Team ID
                                </span>
                            </label>
                        </div>
                    </div>

                    <!-- Conditional Inputs -->
                    <div v-if="syncForm.sync_type === 'sub_id'" class="mt-4">
                        <label
                            for="sub_id"
                            class="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Subscription ID
                        </label>
                        <input
                            id="sub_id"
                            v-model="syncForm.sub_id"
                            type="text"
                            placeholder="sub_123456"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>

                    <div v-if="syncForm.sync_type === 'team_id'" class="mt-4">
                        <label
                            for="team_id"
                            class="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Team ID
                        </label>
                        <input
                            id="team_id"
                            v-model.number="syncForm.team_id"
                            type="number"
                            placeholder="123"
                            min="1"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                <div class="mt-6 flex justify-start">
                    <AtomicButton
                        color="blue"
                        size="md"
                        :disabled="isSyncing || !syncForm.sync_type"
                        :loading="isSyncing"
                        @click="handleSyncSubscription"
                    >
                        <template #icon>
                            <ArrowPathIcon class="h-5 w-5" />
                        </template>
                        {{
                            isSyncing ? "Syncing..." : "Sync Subscription Data"
                        }}
                    </AtomicButton>
                </div>
            </div>
        </div>

        <!-- Artisan Commands Section -->
        <div class="mt-10">
            <div class="rounded-lg bg-white p-6 shadow w-full max-w-full">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    Artisan Commands
                </h2>
                <p class="text-sm text-gray-600 mb-6">
                    Execute custom application commands directly from the kiosk
                    interface. Laravel built-in commands are filtered out for
                    security.
                </p>

                <div v-if="artisanCommands.length > 0" class="overflow-x-auto">
                    <div
                        class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                    >
                        <table class="w-full divide-y divide-gray-300">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        Command
                                    </th>
                                    <th
                                        scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                    >
                                        Description
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 bg-white">
                                <tr
                                    v-for="command in artisanCommands"
                                    :key="command.name"
                                    class="hover:bg-gray-50"
                                >
                                    <td
                                        class="px-6 py-4 text-sm font-mono text-gray-900 max-w-xs"
                                    >
                                        {{ command.name }}
                                    </td>
                                    <td
                                        class="px-6 py-4 text-sm text-gray-900 max-w-lg"
                                    >
                                        {{
                                            command.description ||
                                            "No description available"
                                        }}
                                    </td>
                                    <td
                                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                                    >
                                        <AtomicButton
                                            color="green"
                                            size="sm"
                                            :disabled="
                                                executingCommand ===
                                                command.name
                                            "
                                            :loading="
                                                executingCommand ===
                                                command.name
                                            "
                                            @click="
                                                showConfirmationModal(command)
                                            "
                                        >
                                            <template #icon>
                                                <PlayIcon class="h-5 w-5" />
                                            </template>
                                            Execute
                                        </AtomicButton>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-else class="text-center py-8 text-gray-500">
                    No custom artisan commands available
                </div>
            </div>
        </div>

        <!-- Confirmation Modal -->
        <TransitionRoot as="template" :show="showModal">
            <Dialog as="div" class="relative z-50" @close="showModal = false">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div
                        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    />
                </TransitionChild>

                <div class="fixed inset-0 z-10 overflow-y-auto">
                    <div
                        class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                    >
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-300"
                            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leave-from="opacity-100 translate-y-0 sm:scale-100"
                            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
                            >
                                <div>
                                    <div
                                        class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100"
                                    >
                                        <ExclamationTriangleIcon
                                            class="h-6 w-6 text-yellow-600"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle
                                            as="h3"
                                            class="text-base font-semibold leading-6 text-gray-900"
                                        >
                                            Confirm Command Execution
                                        </DialogTitle>
                                        <div class="mt-2">
                                            <p class="text-sm text-gray-500">
                                                Are you sure you want to execute
                                                the command:
                                            </p>
                                            <p
                                                class="text-sm font-mono bg-gray-100 p-2 mt-2 rounded"
                                            >
                                                {{ selectedCommand?.name }}
                                            </p>
                                            <p
                                                class="text-sm text-gray-500 mt-2"
                                            >
                                                {{
                                                    selectedCommand?.description
                                                }}
                                            </p>
                                            <p
                                                class="text-xs text-red-600 mt-3"
                                            >
                                                This action cannot be undone and
                                                will execute immediately.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3"
                                >
                                    <AtomicButton
                                        color="red"
                                        size="md"
                                        :disabled="
                                            executingCommand ===
                                            selectedCommand?.name
                                        "
                                        :loading="
                                            executingCommand ===
                                            selectedCommand?.name
                                        "
                                        class="sm:col-start-2"
                                        @click="executeCommand"
                                    >
                                        <template #icon>
                                            <PlayIcon class="h-5 w-5" />
                                        </template>
                                        Execute Command
                                    </AtomicButton>
                                    <AtomicButton
                                        color="gray"
                                        size="md"
                                        class="mt-3 sm:col-start-1 sm:mt-0"
                                        :disabled="
                                            executingCommand ===
                                            selectedCommand?.name
                                        "
                                        @click="showModal = false"
                                    >
                                        Cancel
                                    </AtomicButton>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script lang="ts" setup>
import {
    PaperAirplaneIcon,
    ExclamationTriangleIcon,
    UserGroupIcon,
    ArrowPathIcon,
    PlayIcon,
} from "@heroicons/vue/24/outline";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { useForm } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import { ref, computed, defineProps } from "vue";

interface LogEntry {
    id: number;
    type: "success" | "error";
    message: string;
    timestamp: string;
}

interface NotificationData {
    id: number;
    name: string;
    description: string;
    frequency: string;
    team_name: string;
    last_sent_at: string | null;
    next_send_at: string | null;
    is_active: boolean;
    recipients_count: number;
}

interface AlertSettingData {
    id: number;
    team_id: number;
    team_name: string;
    compliance_drop: number;
    time_frame: number;
    time_frame_label: string;
    enabled: boolean;
    last_sent_at: string | null;
    created_at: string | null;
}

interface ArtisanCommandData {
    name: string;
    description: string;
}

defineProps<{
    notifications: NotificationData[];
    alertSettings: AlertSettingData[];
    artisanCommands: ArtisanCommandData[];
}>();

// Get access to translations
const { t } = useTranslations();

// Helper function to format dates
const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
};

// Form for sending notifications
const form = useForm({
    notification_id: 0,
});

// Form for bulk subscription
const bulkSubscribeForm = useForm({});

// Form for subscription sync
const syncForm = useForm({
    sync_type: "",
    sub_id: "",
    team_id: null as number | null,
});

// Form for sending alerts
const alertForm = useForm({
    alert_setting_id: 0,
});

// State for tracking which notification is being sent
const sendingNotificationId = ref<number | null>(null);

// State for tracking which alert is being sent
const sendingAlertId = ref<number | null>(null);

// State for tracking alert simulation
const simulatingAlerts = ref<boolean>(false);
const simulationResults = ref<any>(null);

// State for tracking bulk subscription
const isBulkSubscribing = ref<boolean>(false);

// State for tracking subscription sync
const isSyncing = ref<boolean>(false);

// Form for executing commands
const commandForm = useForm({
    command: "",
});

// State for tracking command execution
const executingCommand = ref<string | null>(null);

// Modal state for artisan commands
const showModal = ref<boolean>(false);
const selectedCommand = ref<ArtisanCommandData | null>(null);

// Local state for activity logs
const activityLogs = ref<LogEntry[]>([]);
const logCounter = ref(0);

const recentLogs = computed(() => {
    return activityLogs.value.slice(-5).reverse(); // Show last 5, most recent first
});

const addLog = (type: "success" | "error", message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    activityLogs.value.push({
        id: ++logCounter.value,
        type,
        message,
        timestamp,
    });
};

const handleSendNotification = (notification: NotificationData) => {
    sendingNotificationId.value = notification.id;
    form.notification_id = notification.id;

    form.post(route("kiosk.tools.send-report-notification"), {
        preserveScroll: true,
        onSuccess: (page: any) => {
            const successMessage = page.props?.flash?.success;
            if (successMessage) {
                create({
                    title: t("vue.notification_sent_successfully"),
                    text: successMessage as string,
                    type: "success",
                });
                addLog("success", `"${notification.name}" sent successfully`);
            }
            sendingNotificationId.value = null;
        },
        onError: (errors: any) => {
            const errorMessage =
                errors.notification_id || t("vue.notification_send_failed");
            create({
                title: t("vue.notification_send_failed"),
                text: errorMessage,
                type: "error",
            });
            addLog(
                "error",
                `Failed to send "${notification.name}": ${errorMessage}`,
            );
            sendingNotificationId.value = null;
        },
        onFinish: () => {
            sendingNotificationId.value = null;
        },
    });
};

const handleBulkSubscribe = () => {
    isBulkSubscribing.value = true;

    bulkSubscribeForm.post(route("kiosk.tools.bulk-subscribe-ecomail"), {
        preserveScroll: true,
        onSuccess: (page: any) => {
            const successMessage = page.props?.flash?.success;
            if (successMessage) {
                create({
                    title: "Bulk Subscription Initiated",
                    text: successMessage as string,
                    type: "success",
                });
                addLog(
                    "success",
                    "Bulk Ecomail subscription initiated successfully",
                );
            }
            isBulkSubscribing.value = false;
        },
        onError: (errors: any) => {
            const errorMessage =
                errors.bulk_subscribe || "Failed to initiate bulk subscription";
            create({
                title: "Bulk Subscription Failed",
                text: errorMessage,
                type: "error",
            });
            addLog(
                "error",
                `Failed to initiate bulk subscription: ${errorMessage}`,
            );
            isBulkSubscribing.value = false;
        },
        onFinish: () => {
            isBulkSubscribing.value = false;
        },
    });
};

const handleSendAlert = (alertSetting: AlertSettingData) => {
    sendingAlertId.value = alertSetting.id;
    alertForm.alert_setting_id = alertSetting.id;

    alertForm.post(route("kiosk.tools.send-alert"), {
        preserveScroll: true,
        onSuccess: (page: any) => {
            const successMessage = page.props?.flash?.success;
            if (successMessage) {
                create({
                    title: t("vue.alert_sent_successfully"),
                    text: successMessage as string,
                    type: "success",
                });
                addLog(
                    "success",
                    `Alert sent for "${alertSetting.team_name}" team`,
                );
            }
            sendingAlertId.value = null;
        },
        onError: (errors: any) => {
            const errorMessage =
                errors.alert_setting_id || t("vue.alert_send_failed");
            create({
                title: t("vue.alert_send_failed"),
                text: errorMessage,
                type: "error",
            });
            addLog(
                "error",
                `Failed to send alert for "${alertSetting.team_name}" team: ${errorMessage}`,
            );
            sendingAlertId.value = null;
        },
        onFinish: () => {
            sendingAlertId.value = null;
        },
    });
};

const handleSimulateAlerts = () => {
    simulatingAlerts.value = true;

    // Make a GET request to the simulation endpoint
    fetch(route("kiosk.alerts.simulate"), {
        method: "GET",
        headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
        },
    })
        .then((response) => response.json() as Promise<any>)
        .then((data) => {
            if (data.data) {
                simulationResults.value = data.data;
                create({
                    title: t("vue.alert_simulation_completed"),
                    text: `${t("vue.total_alerts_processed")}: ${data.data.total_alerts_processed}, ${t("vue.total_logs_to_be_created")}: ${data.data.total_logs_to_be_created}, ${t("vue.total_emails_to_be_sent")}: ${data.data.total_emails_to_be_sent}`,
                    type: "success",
                });
                addLog(
                    "success",
                    `Alert simulation completed: ${data.data.total_alerts_processed} alerts processed, ${data.data.total_logs_to_be_created} logs would be created, ${data.data.total_emails_to_be_sent} emails would be sent`,
                );
            } else {
                throw new Error(data.message || "Simulation failed");
            }
        })
        .catch((error) => {
            create({
                title: t("vue.alert_simulation_failed"),
                text: error.message || t("vue.alert_simulation_failed"),
                type: "error",
            });
            addLog(
                "error",
                `Alert simulation failed: ${error.message || "Unknown error"}`,
            );
        })
        .finally(() => {
            simulatingAlerts.value = false;
        });
};

const handleSyncSubscription = () => {
    isSyncing.value = true;

    syncForm.post(route("kiosk.tools.sync-subscription-data"), {
        preserveScroll: true,
        onSuccess: (page: any) => {
            const successMessage = page.props?.flash?.success;
            if (successMessage) {
                create({
                    title: "Subscription Sync Completed",
                    text: successMessage as string,
                    type: "success",
                });
                addLog(
                    "success",
                    `Subscription sync completed: ${successMessage}`,
                );

                // Reset form
                syncForm.reset();
            }
            isSyncing.value = false;
        },
        onError: (errors: any) => {
            const errorMessage =
                errors.subscription_sync || "Failed to sync subscription data";
            create({
                title: "Subscription Sync Failed",
                text: errorMessage,
                type: "error",
            });
            addLog("error", `Subscription sync failed: ${errorMessage}`);
            isSyncing.value = false;
        },
        onFinish: () => {
            isSyncing.value = false;
        },
    });
};

const showConfirmationModal = (command: ArtisanCommandData) => {
    selectedCommand.value = command;
    showModal.value = true;
};

const executeCommand = () => {
    if (!selectedCommand.value) return;

    executingCommand.value = selectedCommand.value.name;
    commandForm.command = selectedCommand.value.name;
    showModal.value = false;

    commandForm.post(route("kiosk.tools.execute-artisan-command"), {
        preserveScroll: true,
        onSuccess: (page: any) => {
            const successMessage = page.props?.flash?.success;
            if (successMessage) {
                create({
                    title: "Command Executed Successfully",
                    text: successMessage as string,
                    type: "success",
                });
                addLog(
                    "success",
                    `Command "${selectedCommand.value?.name}" executed successfully`,
                );
            }
            executingCommand.value = null;
        },
        onError: (errors: any) => {
            const errorMessage = errors.command || "Failed to execute command";
            create({
                title: "Command Execution Failed",
                text: errorMessage,
                type: "error",
            });
            addLog(
                "error",
                `Failed to execute "${selectedCommand.value?.name}": ${errorMessage}`,
            );
            executingCommand.value = null;
        },
        onFinish: () => {
            executingCommand.value = null;
            selectedCommand.value = null;
        },
    });
};
</script>

<style></style>
